import { useEffect, useState, type ReactNode } from 'react';
import { HiOutlineCheck, HiOutlineChevronDown, HiOutlineTrash } from 'react-icons/hi2';
import type { AiProviderStatusId } from '../ai-platform/role-config.js';
import {
  emptyProviderCredentialForm,
  maskApiKeyPreview,
  providerCapabilityLabel,
  providerCredentialBadgeKind,
  providerCredentialBadgeLabel,
  providerCredentialLabel,
  providerNeedsFolderId,
  shouldShowDeleteEncryptedCredential,
} from './providerStatus.js';
import type { ProviderCredentialsPanelProps } from './types.js';

type ProviderFormState = ReturnType<typeof emptyProviderCredentialForm>;

const DEFAULT_LABELS = {
  apiKeyPlaceholder: 'Новый API-ключ',
  folderIdPlaceholder: 'Folder ID',
  loadingPlaceholder: 'Загрузка…',
  saveLabel: 'Сохранить',
  deleteConfirm: 'Удалить сохранённый ключ провайдера?',
  deleteBusy: 'Удаляю сохранённый ключ…',
  saveBusy: 'Сохраняю ключ…',
  loadError: 'Не удалось загрузить ключ',
  actionError: 'Ошибка интеграции',
  success: 'Готово',
};

function DefaultTooltip({ text, children }: { text: string; children: ReactNode }) {
  return <span title={text}>{children}</span>;
}

export function ProviderCredentialsPanel({
  providers,
  api,
  busy,
  onBusyChange,
  onNotify,
  onReloadProviders,
  classNamePrefix = 'admin-ai-provider',
  labels,
  renderTooltip,
}: ProviderCredentialsPanelProps) {
  const mergedLabels = { ...DEFAULT_LABELS, ...labels };
  const Tooltip = renderTooltip
    ? ({ text, children }: { text: string; children: ReactNode }) => <>{renderTooltip(text, children)}</>
    : DefaultTooltip;

  const [openProvider, setOpenProvider] = useState<AiProviderStatusId | null>(null);
  const [forms, setForms] = useState<Record<string, ProviderFormState>>({});

  useEffect(() => {
    setForms((current) => {
      let changed = false;
      const next = { ...current };
      for (const provider of providers) {
        const form = next[provider.id];
        if (!form) continue;
        if (!provider.configured && provider.source !== 'encrypted') {
          if (form.apiKey || form.folderId) {
            next[provider.id] = emptyProviderCredentialForm();
            changed = true;
          }
        }
      }
      return changed ? next : current;
    });
  }, [providers.map((provider) => `${provider.id}:${provider.source}:${provider.configured}`).join('|')]);

  const updateForm = (providerId: AiProviderStatusId, patch: Partial<ProviderFormState>) => {
    setForms((current) => ({
      ...current,
      [providerId]: {
        apiKey: current[providerId]?.apiKey ?? '',
        folderId: current[providerId]?.folderId ?? '',
        ...patch,
      },
    }));
  };

  const toggleProvider = (providerId: AiProviderStatusId) => {
    setOpenProvider((current) => (current === providerId ? null : providerId));
  };

  const run = async (label: string, action: () => Promise<void>) => {
    onBusyChange(true);
    onNotify(label, 'info');
    try {
      await action();
      onNotify(mergedLabels.success, 'success');
    } catch (error) {
      onNotify(error instanceof Error ? error.message : mergedLabels.actionError, 'error');
    } finally {
      onBusyChange(false);
    }
  };

  return (
    <div className={`${classNamePrefix}-list`}>
      {providers.map((provider) => {
        const form = forms[provider.id] ?? emptyProviderCredentialForm();
        const isOpen = openProvider === provider.id;
        const badgeKind = providerCredentialBadgeKind(provider);
        const label = providerCredentialLabel(provider.id);
        const fieldsBusy = busy;
        const keyPreview = form.apiKey.trim()
          ? maskApiKeyPreview(form.apiKey)
          : provider.configured
            ? '••••••••'
            : '—';
        const capabilityLabel = providerCapabilityLabel(provider.capabilities[0] ?? 'text');
        return (
          <article
            key={provider.id}
            className={`${classNamePrefix}-row${isOpen ? ` ${classNamePrefix}-row--open` : ''}`}
          >
            <div
              className={`${classNamePrefix}-row__head`}
              role="button"
              tabIndex={0}
              aria-expanded={isOpen}
              onClick={() => toggleProvider(provider.id)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  toggleProvider(provider.id);
                }
              }}
            >
              <span className={`${classNamePrefix}-row__main`}>
                <span className={`${classNamePrefix}-row__name`}>{label}</span>
                <span className={`${classNamePrefix}-row__meta`}>{keyPreview}</span>
                <span className={`${classNamePrefix}-row__capability`}>{capabilityLabel}</span>
              </span>
              <span className={`${classNamePrefix}-row__actions`}>
                <span className={`${classNamePrefix}-badge ${classNamePrefix}-badge--${badgeKind}`}>
                  {providerCredentialBadgeLabel(provider)}
                </span>
                {shouldShowDeleteEncryptedCredential(provider) ? (
                  <Tooltip text="Удалить только сохранённый в БД ключ. Env fallback останется, если задан.">
                    <button
                      type="button"
                      className={`${classNamePrefix}-icon-btn ${classNamePrefix}-icon-btn--danger`}
                      disabled={busy}
                      aria-label={`Удалить ключ ${label}`}
                      onClick={(event) => {
                        event.stopPropagation();
                        void run(mergedLabels.deleteBusy, async () => {
                          if (!window.confirm(mergedLabels.deleteConfirm)) return;
                          await api.deleteCredential(provider.id);
                          setForms((current) => ({
                            ...current,
                            [provider.id]: emptyProviderCredentialForm(),
                          }));
                          await onReloadProviders();
                        });
                      }}
                    >
                      <HiOutlineTrash aria-hidden="true" />
                    </button>
                  </Tooltip>
                ) : null}
                <button
                  type="button"
                  className={`${classNamePrefix}-icon-btn`}
                  disabled={busy}
                  aria-label={isOpen ? `Свернуть ${label}` : `Настроить ${label}`}
                  aria-expanded={isOpen}
                  onClick={(event) => {
                    event.stopPropagation();
                    toggleProvider(provider.id);
                  }}
                >
                  <HiOutlineChevronDown
                    aria-hidden="true"
                    className={`${classNamePrefix}-row__chevron${isOpen ? ` ${classNamePrefix}-row__chevron--open` : ''}`}
                  />
                </button>
              </span>
            </div>

            {isOpen ? (
              <div className={`${classNamePrefix}-row__editor`}>
                {provider.configured ? (
                  <p className={`${classNamePrefix}-row__hint`}>
                    Сохранённый ключ не показывается. Введите новый только если хотите заменить его.
                  </p>
                ) : null}
                <div className={`${classNamePrefix}-row__editor-grid`}>
                  <label className="admin-field admin-field--flush">
                    <span className="u-visually-hidden">{mergedLabels.apiKeyPlaceholder}</span>
                    <input
                      type="password"
                      autoComplete="new-password"
                      spellCheck={false}
                      placeholder={mergedLabels.apiKeyPlaceholder}
                      value={form.apiKey}
                      disabled={fieldsBusy}
                      onChange={(e) => updateForm(provider.id, { apiKey: e.target.value })}
                    />
                  </label>
                  <button
                    type="button"
                    className={`button button--primary button--small ${classNamePrefix}-save-btn`}
                    disabled={
                      fieldsBusy ||
                      !form.apiKey.trim() ||
                      (providerNeedsFolderId(provider.id) && !form.folderId.trim())
                    }
                    onClick={() =>
                      run(mergedLabels.saveBusy, async () => {
                        const body = providerNeedsFolderId(provider.id)
                          ? { apiKey: form.apiKey.trim(), folderId: form.folderId.trim() }
                          : { apiKey: form.apiKey.trim() };
                        await api.saveCredential(provider.id, body);
                        setForms((current) => ({
                          ...current,
                          [provider.id]: emptyProviderCredentialForm(),
                        }));
                        await onReloadProviders();
                      })
                    }
                  >
                    <HiOutlineCheck aria-hidden="true" />
                    <span className="u-visually-hidden">{mergedLabels.saveLabel}</span>
                  </button>
                </div>
                {providerNeedsFolderId(provider.id) ? (
                  <label className="admin-field admin-field--flush">
                    <span className="u-visually-hidden">{mergedLabels.folderIdPlaceholder}</span>
                    <input
                      type="text"
                      autoComplete="off"
                      spellCheck={false}
                      placeholder={mergedLabels.folderIdPlaceholder}
                      value={form.folderId}
                      disabled={fieldsBusy}
                      onChange={(e) => updateForm(provider.id, { folderId: e.target.value })}
                    />
                  </label>
                ) : null}
              </div>
            ) : null}
          </article>
        );
      })}
    </div>
  );
}
